/**
 * Wachtwoordeisen, gedeeld tussen client en worker zodat het scherm precies
 * dezelfde uitkomst laat zien als wat de server afdwingt.
 *
 * De keuzes volgen de OWASP Authentication Cheat Sheet en ASVS 5.0 V6.2:
 *
 * - **Minimaal 15 tekens.** ASVS 6.2.1 vraagt acht als ondergrens, maar noemt
 *   vijftien uitdrukkelijk als sterke aanbeveling en de Cheat Sheet rekent
 *   alles onder de vijftien als zwak *zolang er geen tweede factor is*. Die
 *   hebben wij niet, dus vijftien.
 * - **Geen samenstellingsregels** (ASVS 6.2.5). Geen verplichte hoofdletter,
 *   cijfer of teken: dat levert `Welkom2026!` op, wat lang niet zo sterk is als
 *   vier willekeurige woorden achter elkaar.
 * - **Alles mag**, inclusief spaties en unicode, zodat een wachtwoordzin kan.
 * - **Bovengrens 64 tekens** (ASVS 6.2.9 wil er minstens 64 toestaan).
 *
 * Wat hier *niet* staat: de controle tegen uitgelekte wachtwoorden (ASVS
 * 6.2.12). Die vraagt een netwerkverzoek en hoort dus aan de serverkant, zie
 * `worker/src/lib/pwned.ts`.
 */
export const WACHTWOORD_MIN_LENGTE = 15;
export const WACHTWOORD_MAX_LENGTE = 64;
/**
 * bcrypt kijkt niet verder dan 72 *bytes* en negeert de rest stilzwijgend. Bij
 * ASCII kan dat niet botsen met de 64 tekens hierboven, maar een zin met
 * emoji of accenten wel: die kost meerdere bytes per teken. Dan liever een
 * duidelijke melding dan een wachtwoord waarvan het staartje niet meetelt.
 */
export const WACHTWOORD_MAX_BYTES = 72;
/** Woorden die in deze context niets bijdragen; als het wachtwoord er (bijna)
 *  alleen uit bestaat is het geraden voor iemand die weet waar hij inbreekt. */
const VERBODEN_WOORDEN = [
    'veerenstael',
    'wagenpark',
    'wachtwoord',
    'password',
    'welkom',
    'welcome',
    'geheim',
    'qwerty',
    'letmein',
];
/** Aantal UTF-8 bytes. Zelf tellen in plaats van `TextEncoder`, want deze laag
 *  is pure domeinlogica en leunt niet op een runtime-API. `for...of` loopt over
 *  code points, dus een emoji telt als één teken van vier bytes. */
function byteLengte(waarde) {
    let bytes = 0;
    for (const teken of waarde) {
        const cp = teken.codePointAt(0) ?? 0;
        bytes += cp < 0x80 ? 1 : cp < 0x800 ? 2 : cp < 0x10000 ? 3 : 4;
    }
    return bytes;
}
/** Alles wat op letters lijkt eruit, zodat `V33r3nst@el` ook wordt gezien. */
function vereenvoudig(waarde) {
    return waarde
        .toLowerCase()
        .replace(/[4@]/g, 'a')
        .replace(/[3]/g, 'e')
        .replace(/[1!|]/g, 'i')
        .replace(/[0]/g, 'o')
        .replace(/[5$]/g, 's')
        .replace(/[7]/g, 't')
        .replace(/[^a-z]/g, '');
}
/**
 * Toetst een wachtwoord aan de regels hierboven en geeft *alle* bezwaren terug,
 * niet alleen de eerste. Eén foutmelding per keer laat iemand net zo lang
 * gokken tot het goed is; alles tegelijk is één keer lezen en dan raak.
 */
export function keurWachtwoord(wachtwoord, context = {}) {
    const fouten = [];
    if (wachtwoord.length < WACHTWOORD_MIN_LENGTE) {
        fouten.push(`Gebruik minimaal ${WACHTWOORD_MIN_LENGTE} tekens.`);
    }
    if (wachtwoord.length > WACHTWOORD_MAX_LENGTE) {
        fouten.push(`Gebruik maximaal ${WACHTWOORD_MAX_LENGTE} tekens.`);
    }
    else if (byteLengte(wachtwoord) > WACHTWOORD_MAX_BYTES) {
        fouten.push('Dit wachtwoord is te lang door speciale tekens. Maak het wat korter.');
    }
    const kaal = vereenvoudig(wachtwoord);
    const lokaalDeel = context.email?.split('@')[0] ?? '';
    const eigenWoorden = [lokaalDeel, ...(context.naam ?? '').split(/\s+/)]
        .map(vereenvoudig)
        .filter((w) => w.length >= 4);
    if (eigenWoorden.some((w) => kaal.includes(w))) {
        fouten.push('Gebruik niet je naam of e-mailadres in het wachtwoord.');
    }
    if (VERBODEN_WOORDEN.some((w) => kaal.includes(w))) {
        fouten.push('Dit wachtwoord bevat een te voorspelbaar woord.');
    }
    // Eén teken of één kort stukje dat zich herhaalt telt als raadbaar, hoe lang
    // het geheel ook is: `abcabcabcabcabc` haalt de vijftien tekens wel maar is
    // in de praktijk drie tekens werk.
    if (/^(.{1,4})\1+$/.test(wachtwoord)) {
        fouten.push('Dit wachtwoord is een herhaling van hetzelfde stukje.');
    }
    return fouten;
}
